import React, { useState } from 'react';
import { useRecoilState } from 'recoil';
import { Container, Row, Col, Card, CardBody, CardTitle, Input, Button, Badge, Table } from 'reactstrap';
import moment from 'moment';
import { findByRegistrationRequest } from '../../../api/controller/VesselFinderController';
import { vesselFinderResultState, vesselFinderResultsState, vesselFinderSearchState } from './state';
import { Loader } from '../../../custom/components/Loader';

const statusColor = (s) => ({ APPROVED: 'success', PENDING: 'warning', REJECTED: 'danger' }[s] ?? 'secondary');

const VesselFinder = () => {
    const [search, setSearch] = useRecoilState(vesselFinderSearchState);
    const [vessel, setVessel] = useRecoilState(vesselFinderResultState);
    const [results, setResults] = useRecoilState(vesselFinderResultsState);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async () => {
        if (!search.trim()) return;
        setLoading(true);
        setError('');
        const res = await findByRegistrationRequest({ SearchQuery: search.trim() });
        setLoading(false);
        setSearched(true);
        if (!res || res.Status === 'error') {
            setError(res?.Message || 'Search failed.');
            setResults([]);
            setVessel(null);
            return;
        }
        const found = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        setResults(found);
        // One hit needs no choosing; several do.
        setVessel(found.length === 1 ? found[0] : null);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <h4 className="mb-4">Vessel Finder</h4>

                    <Card className="mb-4">
                        <CardBody>
                            <Row className="align-items-center">
                                <Col md={5}>
                                    <Input
                                        placeholder="Enter vessel name or registration number..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                    />
                                </Col>
                                <Col md="auto" className="mt-2 mt-md-0">
                                    <Button color="primary" onClick={handleSearch} disabled={loading}>
                                        {loading ? 'Searching...' : 'Search'}
                                    </Button>
                                </Col>
                            </Row>
                            {error && <p className="text-danger mt-2 mb-0">{error}</p>}
                        </CardBody>
                    </Card>

                    {loading && <Loader />}

                    {!loading && searched && results.length === 0 && (
                        <Card><CardBody><p className="text-muted mb-0">No vessel found matching that name or registration number.</p></CardBody></Card>
                    )}

                    {!loading && results.length > 1 && (
                        <Card className="mb-3">
                            <CardBody>
                                <CardTitle tag="h6" className="mb-3">{results.length} matches — select one</CardTitle>
                                <Table hover size="sm" className="mb-0">
                                    <thead>
                                        <tr><th>Registration No.</th><th>Vessel</th><th>Owner</th><th>Status</th></tr>
                                    </thead>
                                    <tbody>
                                        {results.map(r => (
                                            <tr
                                                key={r.id}
                                                onClick={() => setVessel(r)}
                                                style={{ cursor: 'pointer' }}
                                                className={vessel?.id === r.id ? 'table-active' : ''}
                                            >
                                                <td>{r.registration_number}</td>
                                                <td>
                                                    {r.name}
                                                    {r.is_deleted && <Badge color="dark" className="ms-2">Deleted</Badge>}
                                                </td>
                                                <td>{r.owner?.name ?? '-'}</td>
                                                <td><Badge color={statusColor(r.status)}>{r.status}</Badge></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    )}

                    {!loading && vessel && (
                        <>
                            <Card className="mb-3">
                                <CardBody>
                                    <Row className="mb-2 align-items-center">
                                        <Col><CardTitle tag="h5" className="mb-0">{vessel.name}</CardTitle></Col>
                                        <Col xs="auto">
                                            <Badge color={statusColor(vessel.status)} className="fs-6 px-3 py-2">{vessel.status}</Badge>
                                            {vessel.active && <Badge color="info" className="ms-2 fs-6 px-3 py-2">Active</Badge>}
                                            {vessel.is_deleted && <Badge color="dark" className="ms-2 fs-6 px-3 py-2">Deleted</Badge>}
                                        </Col>
                                    </Row>
                                    <Table bordered size="sm" className="mb-0">
                                        <tbody>
                                            <tr><th style={{width:'35%'}}>Registration No.</th><td>{vessel.registration_number}</td></tr>
                                            <tr><th>Vessel Type</th><td>{vessel.vessel_type?.name ?? '-'}</td></tr>
                                            <tr><th>Owner</th><td>{vessel.owner?.name} ({vessel.owner?.email})</td></tr>
                                            <tr><th>Representative</th><td>{vessel.representative_name} — {vessel.representative_contact_number}</td></tr>
                                            <tr><th>Length / Breadth</th><td>{vessel.length}m / {vessel.breadth}m</td></tr>
                                            <tr><th>Gross Tonnage</th><td>{vessel.gross_tonnage}</td></tr>
                                            <tr><th>Approved By</th><td>{vessel.approver ? `${vessel.approver.name} on ${vessel.approved_at_label}` : '-'}</td></tr>
                                            {vessel.rejection_reason && <tr><th>Rejection Reason</th><td className="text-danger">{vessel.rejection_reason}</td></tr>}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>

                            {vessel.active_permit && (
                                <Card className="mb-3 border-success">
                                    <CardBody>
                                        <CardTitle tag="h6" className="text-success">Active Permit</CardTitle>
                                        <Table bordered size="sm" className="mb-0">
                                            <tbody>
                                                <tr><th style={{width:'35%'}}>Type</th><td>{vessel.active_permit.permit_type?.name}</td></tr>
                                                <tr><th>Effective</th><td>{vessel.active_permit.permit_effective_display}</td></tr>
                                                <tr><th>Expires</th><td>{vessel.active_permit.permit_expiry_display}</td></tr>
                                            </tbody>
                                        </Table>
                                    </CardBody>
                                </Card>
                            )}

                            <Card className="mb-3">
                                <CardBody>
                                    <CardTitle tag="h6">Permit History ({vessel.all_permits?.length ?? 0})</CardTitle>
                                    {(!vessel.all_permits || vessel.all_permits.length === 0) ? (
                                        <p className="text-muted mb-0">No permits on record.</p>
                                    ) : (
                                        <Table bordered size="sm" className="mb-0">
                                            <thead><tr><th>#</th><th>Type</th><th>Effective</th><th>Expires</th><th>Status</th></tr></thead>
                                            <tbody>
                                                {vessel.all_permits.map((p, i) => {
                                                    const now = moment();
                                                    const active = moment(p.effective_at).isBefore(now) && moment(p.expires_at).isAfter(now);
                                                    const expired = moment(p.expires_at).isBefore(now);
                                                    return (
                                                        <tr key={p.id}>
                                                            <td>{i + 1}</td>
                                                            <td>{p.permit_type?.name ?? '-'}</td>
                                                            <td>{p.permit_effective_display}</td>
                                                            <td>{p.permit_expiry_display}</td>
                                                            <td>
                                                                <Badge color={active ? 'success' : expired ? 'secondary' : 'warning'}>
                                                                    {active ? 'Active' : expired ? 'Expired' : 'Upcoming'}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </Card>

                            <Card className="mb-3">
                                <CardBody>
                                    <CardTitle tag="h6">Status History ({vessel.status_events?.length ?? 0})</CardTitle>
                                    {(!vessel.status_events || vessel.status_events.length === 0) ? (
                                        <p className="text-muted mb-0">No status events.</p>
                                    ) : (
                                        <Table bordered size="sm" className="mb-0">
                                            <thead><tr><th>Status</th><th>Date</th></tr></thead>
                                            <tbody>
                                                {vessel.status_events.map(e => (
                                                    <tr key={e.id}>
                                                        <td><Badge color={statusColor(e.event)}>{e.event}</Badge></td>
                                                        <td>{moment(e.created_at).format('D MMM YYYY, HH:mm')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </Card>

                            <Card className="mb-3">
                                <CardBody>
                                    <CardTitle tag="h6">Vessel Users ({vessel.vessel_users?.length ?? 0})</CardTitle>
                                    {(!vessel.vessel_users || vessel.vessel_users.length === 0) ? (
                                        <p className="text-muted mb-0">No users.</p>
                                    ) : (
                                        <Table bordered size="sm" className="mb-0">
                                            <thead><tr><th>Name</th><th>Email</th><th>Status</th></tr></thead>
                                            <tbody>
                                                {vessel.vessel_users.map(vu => (
                                                    <tr key={vu.id}>
                                                        <td>{vu.user?.name ?? '-'}</td>
                                                        <td>{vu.user?.email ?? '-'}</td>
                                                        <td><Badge color={vu.approved ? 'success' : 'warning'}>{vu.approved ? 'Approved' : 'Pending'}</Badge></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </Card>
                        </>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VesselFinder;
